<div class="details" [class.tooltip-padding]="isTooltip()">
  @let isTechnicalPoint = RouteHelper.isTechnicalPoint(value());
  @let isDelegationPoint = category() === TechnicalPointCategory.DELEGATION;

  @if (!isTooltip()) {
    @if (!isDelegationPoint && (hasDriverReport() || isCompleted())) {
      <div class="item">
        <onyx-icon name="document-filled" [size]="16"></onyx-icon>

        <div class="content">
          @if (hasDriverReport()) {
            <onyx-link
              color="blue"
              font="f-body-l f-link"
              (click)="openDriverReportModal()"
            >
              {{ 'labels.realizationSummary' | translate }}
            </onyx-link>
          } @else {
            <div class="content tooltip-container">
              <p class="f-muted">
                {{ `${I18N}.missingDriverReport` | translate }}
              </p>

              <onyx-tooltip
                [tooltip]="`${I18N}.missingDriverReportHint`"
              ></onyx-tooltip>
            </div>
          }

          @if (isCompleted() && realizationCrew(); as crew) {
            <span class="crew f-muted">
              <app-module-link
                module="fleet"
                color="gray"
                (linkClick)="openFleetModal(crew.vehicle.uuid)"
              >
                {{ crew.vehicle.data | fleetIdentifier }}
              </app-module-link>

              @if (crew.trailer; as trailer) {
                <app-module-link
                  module="fleet"
                  color="gray"
                  (linkClick)="openFleetModal(trailer.uuid)"
                >
                  {{ trailer.data | fleetIdentifier }}
                </app-module-link>
              }

              @for (driver of crew.drivers; track driver.uuid) {
                <app-module-link
                  module="drivers"
                  color="gray"
                  (linkClick)="openDriverModal(driver.uuid)"
                >
                  {{ driver.data | onyxName }}
                </app-module-link>
              }
            </span>
          }
        </div>
      </div>
    }

    @if (!isTechnicalPoint) {
      <div class="item">
        <onyx-icon
          [name]="
            includedInOrder() ? 'check-circle-filled' : 'minus-circle-filled'
          "
          [size]="16"
        ></onyx-icon>

        <div class="content tooltip-container">
          <p [class.f-muted]="!includedInOrder()">
            {{
              `${I18N}.${includedInOrder() ? 'includedInOrder' : 'notIncludedInOrder'}`
                | translate
            }}
          </p>

          <onyx-tooltip
            [tooltip]="`${I18N}.${includedInOrder() ? 'includedInOrderHint' : 'notIncludedInOrderHint'}`"
          ></onyx-tooltip>
        </div>
      </div>
    }

    @if (context === 'map' && order(); as order) {
      <div class="item">
        <onyx-icon name="contractor-filled" [size]="16"></onyx-icon>

        <button class="f-link" (click)="openSenderModal(order.sender)">
          {{ order.sender.companyProfile.displayName }}
        </button>
      </div>

      <div class="item">
        <onyx-icon name="offer-filled" [size]="16"></onyx-icon>

        <div class="line">
          <span class="f-muted">{{ 'labels.order' | translate }}</span>

          <button class="f-link" (click)="openOrderModal(order.uuid)">
            {{ order.identifier }}
          </button>
        </div>
      </div>
    }
  }

  @if (delegationData(); as data) {
    @for (delegation of data.currentDelegations; track delegation.uuid) {
      <div class="item">
        <onyx-icon name="delegation-filled" [size]="16"></onyx-icon>

        <div class="line">
          <span class="drivers">
            <app-module-link
              module="drivers"
              color="black"
              (linkClick)="openDriverModal(delegation.primaryDriver)"
            >
              {{ delegation.primaryDriver.driverData | onyxName }}
            </app-module-link>

            @if (delegation.secondaryDriver; as secondaryDriver) {
              <span>,&nbsp;</span>

              <app-module-link
                module="drivers"
                color="black"
                (linkClick)="openDriverModal(secondaryDriver)"
              >
                {{ secondaryDriver.driverData | onyxName }}
              </app-module-link>
            }
          </span>

          <app-module-link
            module="delegations"
            color="gray"
            (linkClick)="openDelegationModal(delegation)"
          >
            {{ delegation.identifier }}
          </app-module-link>
        </div>
      </div>
    }
  }

  @if (transitRoute$() | async; as transitRoute) {
    <div class="item">
      <onyx-icon name="contractor-filled" [size]="16"></onyx-icon>

      <p>{{ transitRoute.operator }}</p>
    </div>

    <div class="item">
      <onyx-icon name="location-filled" [size]="16"></onyx-icon>

      <div class="content">
        @let fromAddress = transitRoute.from.address;
        @let toAddress = transitRoute.to.address;

        <p>
          {{ transitRoute.from.name }},
          {{ fromAddress.countryCode | uppercase }} -
          {{ transitRoute.to.name }}, {{ toAddress.countryCode | uppercase }}
        </p>

        <p class="line f-muted">
          <span>{{ 'labels.from' | translate }}:</span>

          <onyx-address
            [address]="fromAddress"
            format="full"
            [showFlag]="false"
          ></onyx-address>
        </p>

        <p class="line f-muted">
          <span>{{ 'labels.to' | translate }}:</span>

          <onyx-address
            [address]="toAddress"
            format="full"
            [showFlag]="false"
          ></onyx-address>
        </p>
      </div>
    </div>
  } @else {
    <div class="item">
      <onyx-icon name="location-filled" [size]="16"></onyx-icon>

      <div class="content">
        @if (addressName(); as name) {
          @if (poiLink(); as poiLink) {
            <button class="f-link" (click)="poiLink()">
              {{ name | translate }}
            </button>
          } @else {
            <p>{{ name | translate }}</p>
          }
        }

        <onyx-address
          [class.f-muted]="addressName()"
          [address]="address()"
          format="full"
          [showFlag]="false"
        ></onyx-address>
      </div>

      @if (address(); as address) {
        <div class="actions">
          <onyx-icon-button
            variant="transparent"
            color="gray"
            [size]="24"
            onyxTooltip="labels.copyCoordinates"
            (click)="
              actionHelper.copy(AddressHelper.composeCoordinatesLabel(address))
            "
          >
            <onyx-icon name="copy-outlined" [size]="16"></onyx-icon>
          </onyx-icon-button>
        </div>
      }
    </div>
  }

  @if (delegationTimeData(); as data) {
    <div class="item">
      <onyx-icon name="clock-filled" [size]="16"></onyx-icon>

      <div class="content">
        <div class="line">
          <span class="margin f-muted">{{ data.label | translate }}</span>

          {{ data.dateTime | onyxDate: 'time-dot-date' : { separator: ' · ' } }}
        </div>

        @if (data.plannedEnd; as plannedEnd) {
          <div class="line">
            <span class="margin f-muted">
              {{ 'labels.endPlan' | translate }}
            </span>

            {{ plannedEnd | onyxDate: 'time-dot-date' : { separator: ' · ' } }}
          </div>
        }
      </div>
    </div>
  }

  @if (!isTooltip()) {
    @if (!isHandlingPoint() && !isTechnicalPoint) {
      <div class="item">
        <onyx-icon name="telephone-filled" [size]="16"></onyx-icon>

        @if (phone(); as phone) {
          @let formattedPhone = phone | onyxPhone;
          <button class="f-link" (click)="ActionHelper.makeCall(phone)">
            {{ formattedPhone }}
          </button>

          <div class="actions">
            <onyx-icon-button
              variant="transparent"
              color="gray"
              [size]="24"
              onyxTooltip="labels.copy"
              (click)="actionHelper.copy(formattedPhone)"
            >
              <onyx-icon name="copy-outlined" [size]="16"></onyx-icon>
            </onyx-icon-button>
          </div>
        } @else {
          <p class="f-muted">{{ 'labels.missingPhone' | translate }}</p>
        }
      </div>
    }

    @if (swapData(); as swapData) {
      <div class="item">
        <onyx-icon name="exchange-filled" [size]="16"></onyx-icon>

        <onyx-link color="black" (click)="openSwapModal()">
          {{ swapData.identifier }}
        </onyx-link>
      </div>

      <ng-container
        *ngTemplateOutlet="
          swapDataTemplate;
          context: {
            list: swapData.added,
            icon: 'plus-circle-filled',
            label: 'labels.add',
          }
        "
      ></ng-container>

      <ng-container
        *ngTemplateOutlet="
          swapDataTemplate;
          context: {
            list: swapData.removed,
            icon: 'minus-circle-filled',
            label: `${I18N}.move`,
          }
        "
      ></ng-container>
    }

    @if (semiTrailer(); as semiTrailer) {
      <div class="item">
        <onyx-icon name="semi-trailer-loaded-filled" [size]="16"></onyx-icon>

        <span>
          {{ semiTrailer.label | translate }}

          @if (isObject(semiTrailer.data)) {
            <span class="f-muted">
              {{ semiTrailer.data.generalInformation | fleetIdentifier }}
            </span>

            @if (fleetHelper.hasModule()) {
              <div class="actions">
                <onyx-icon-button
                  variant="transparent"
                  color="gray"
                  [size]="24"
                  onyxTooltip="labels.preview"
                  (click)="openFleetModal(semiTrailer.data.uuid)"
                >
                  <onyx-icon name="side-modal-filled" [size]="16"></onyx-icon>
                </onyx-icon-button>
              </div>
            }
          } @else {
            <span class="outsourcing">
              {{ semiTrailer.data | fleetIdentifier }}
            </span>
          }
        </span>
      </div>
    }

    @if (isHandlingPoint() && goods()?.length && goods(); as goods) {
      <div class="item">
        <onyx-icon name="good-filled" [size]="16"></onyx-icon>

        <div class="content">
          @for (good of goods; track good.uuid) {
            <p class="line">
              <span>
                {{
                  good.type ? (good.type.names | onyxLanguage) : good.typeName
                }}
              </span>

              <span class="f-muted">
                {{ good.unit.names | onyxLanguage }}

                @if (good.size) {
                  {{ good | goodQuantity }}
                }

                · {{ good.totalWeight | number }} kg
              </span>
            </p>
          }
        </div>

        <div class="actions">
          <onyx-icon-button
            variant="transparent"
            color="gray"
            [size]="24"
            onyxTooltip="labels.preview"
            (click)="openGoodsPreview()"
          >
            <onyx-icon name="side-modal-filled" [size]="16"></onyx-icon>
          </onyx-icon-button>
        </div>
      </div>
    }

    @if (isHandlingPoint()) {
      <div class="item">
        <onyx-icon name="hashtag-filled" [size]="16"></onyx-icon>

        <p class="line">
          @if (referenceNumber()) {
            <span class="f-muted">
              {{ 'labels.pointReferenceNumber' | translate }}
            </span>
          }

          <span [class.f-muted]="!referenceNumber()">
            {{
              referenceNumber() || ('labels.missingReferenceNumber' | translate)
            }}
          </span>
        </p>

        @if (referenceNumber(); as referenceNumber) {
          <div class="actions">
            <onyx-icon-button
              variant="transparent"
              color="gray"
              [size]="24"
              onyxTooltip="labels.copy"
              (click)="actionHelper.copy(referenceNumber)"
            >
              <onyx-icon name="copy-outlined" [size]="16"></onyx-icon>
            </onyx-icon-button>
          </div>
        }
      </div>
    }

    @if (!isDelegationPoint) {
      <div class="item">
        <onyx-icon name="note-filled" [size]="16"></onyx-icon>

        <p [class.f-muted]="!value().driverNote">
          {{ value().driverNote || ('labels.missingNote' | translate) }}
        </p>
      </div>
    }
  }
</div>

@if (!isDelegationPoint && !isPreview()) {
  <app-route-point-details-status
    [point]="point()"
    [isTooltip]="isTooltip()"
  ></app-route-point-details-status>
}

<ng-template
  let-list="list"
  let-icon="icon"
  let-label="label"
  #swapDataTemplate
>
  @if (list?.length) {
    <div class="item">
      <onyx-icon [name]="icon" [size]="16"></onyx-icon>

      <div class="line">
        <span class="f-muted">{{ label | translate }}</span>

        @for (data of list; track data.label) {
          <div class="swap-data-item">
            <onyx-link color="black" (click)="data.callback()">
              {{ data.label }}
            </onyx-link>

            @if (!$last) {
              <span>,</span>
            }
          </div>
        }
      </div>
    </div>
  }
</ng-template>
